import { PickSampleExt } from "@novorender/api";
import { useCallback, useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { measureActions, selectMeasure, selectMeasurePickSettings } from "features/measure";
import { measureInteractionPositionsRef } from "features/measure/measureInteractionPositionsRef";
import { selectCurrentPickerGeneratedParametricData, selectPicker } from "features/render";
import { Picker } from "features/render/types";
import { applyCameraDistanceToMeasureTolerance } from "features/render/utils";
import { hasMouseSupport } from "utils/misc";

import { mobileMeasureActions, selectDimensionSessionStart } from "./mobileMeasureSlice";
import { useMobileMeasureCursor } from "./useMobileMeasureCursor";

const TAP_SELECT_RADIUS = 40;

export function useMobileDimension() {
    const dispatch = useAppDispatch();
    const {
        state: { view },
    } = useExplorerGlobals();
    const measure = useAppSelector(selectMeasure);
    const measurePickSettings = useAppSelector(selectMeasurePickSettings);
    const allowGeneratedParametric = useAppSelector(selectCurrentPickerGeneratedParametricData);
    const picker = useAppSelector(selectPicker);
    const sessionStart = useAppSelector(selectDimensionSessionStart);

    const measureRef = useRef(measure);
    measureRef.current = measure;

    const isMobile = !hasMouseSupport();
    const isDimensionPicker = picker === Picker.Dimension;

    // Record session start when entering Dimension tool
    useEffect(() => {
        if (isMobile && isDimensionPicker) {
            const nonEmpty = measureRef.current.selectedEntities.filter((e) => e.length > 0).length;
            dispatch(mobileMeasureActions.setDimensionSessionStart(nonEmpty));
        }
    }, [isMobile, isDimensionPicker, dispatch]);

    const onPlace = useCallback(
        async (result: PickSampleExt, _canvasPos: { x: number; y: number }) => {
            if (!view || result.objectId === -1) {
                return;
            }

            dispatch(measureActions.selectHoverObj(undefined));

            const tolerance = applyCameraDistanceToMeasureTolerance(
                result.position,
                view.renderState.camera,
                measurePickSettings,
            );
            dispatch(measureActions.setLoadingBrep(true));
            const entity = await view.measure?.core.pickMeasureEntity(
                result.objectId,
                result.position,
                tolerance,
                allowGeneratedParametric.enabled,
            );
            dispatch(measureActions.setLoadingBrep(false));

            if (!entity?.entity) {
                return;
            }

            // If an existing set already has this exact entity, select it instead of duplicating
            const { selectedEntities, currentIndex } = measureRef.current;
            for (let i = 0; i < selectedEntities.length; i++) {
                if (
                    selectedEntities[i].some(
                        (e) =>
                            e.ObjectId === entity.entity.ObjectId &&
                            e.pathIndex === entity.entity.pathIndex &&
                            e.drawKind === entity.entity.drawKind,
                    )
                ) {
                    dispatch(measureActions.selectMeasureSet(i));
                    return;
                }
            }

            // Each pick creates an independent measurement (one entity per set)
            const currentSet = selectedEntities[currentIndex] || [];
            if (currentSet.length > 0) {
                dispatch(measureActions.forceNewMeasureSet());
            }
            dispatch(measureActions.selectEntity({ entity: entity.entity, pin: true }));
        },
        [view, dispatch, measurePickSettings, allowGeneratedParametric],
    );

    const onDragUpdate = useCallback(
        async (result: PickSampleExt | null) => {
            if (!view) {
                return;
            }
            if (!result || result.objectId === -1) {
                dispatch(measureActions.selectHoverObj(undefined));
                dispatch(mobileMeasureActions.setCursorVisual("noHit"));
                return;
            }

            // Match desktop semantics by classifying hover via measure entity drawKind.
            // This gives reliable edge/face discrimination instead of raw pick sample type.
            const tolerance = applyCameraDistanceToMeasureTolerance(
                result.position,
                view.renderState.camera,
                measurePickSettings,
            );
            const hoverEnt = await view.measure?.core.pickMeasureEntity(
                result.objectId,
                result.position,
                tolerance,
                allowGeneratedParametric.enabled,
            );

            if (hoverEnt?.entity) {
                dispatch(
                    measureActions.selectHoverObj({
                        ...hoverEnt.entity,
                        connectionPoint: hoverEnt.connectionPoint,
                    }),
                );
                dispatch(
                    mobileMeasureActions.setCursorVisual(
                        hoverEnt.entity.drawKind === "edge" ? "pointSnap" : "surfaceSnap",
                    ),
                );
            } else {
                dispatch(measureActions.selectHoverObj(undefined));
                dispatch(
                    mobileMeasureActions.setCursorVisual(result.sampleType === "edge" ? "pointSnap" : "surfaceSnap"),
                );
            }
        },
        [view, dispatch, measurePickSettings, allowGeneratedParametric],
    );

    const onTapElsewhere = useCallback(
        async (canvasPos: { x: number; y: number }) => {
            if (!view) {
                return;
            }

            // Pick at tap position and check if the object belongs to an existing measurement
            const result = await view.pick(canvasPos.x, canvasPos.y, {
                sampleDiscRadius: 8,
                async: false,
            });

            if (result && result.objectId !== -1) {
                const { selectedEntities } = measureRef.current;
                for (let i = 0; i < selectedEntities.length; i++) {
                    if (selectedEntities[i].some((e) => e.ObjectId === result.objectId)) {
                        dispatch(measureActions.selectMeasureSet(i));
                        return;
                    }
                }
            }

            // Also check proximity to 2D button positions
            const positions = measureInteractionPositionsRef.current;
            if (!positions) {
                return;
            }
            for (let idx = 0; idx < positions.remove.length; idx++) {
                const pos = positions.remove[idx];
                if (!pos) {
                    continue;
                }
                const dx = canvasPos.x - pos[0];
                const dy = canvasPos.y - pos[1];
                if (dx * dx + dy * dy < TAP_SELECT_RADIUS * TAP_SELECT_RADIUS) {
                    dispatch(measureActions.selectMeasureSet(idx));
                    return;
                }
            }
        },
        [view, dispatch],
    );

    useMobileMeasureCursor({
        picker: Picker.Dimension,
        onPlace,
        onDragUpdate,
        onTapElsewhere,
        enabled: isMobile,
        persistent: true,
    });

    return { sessionStart };
}
